"use client";
//? Library Imports ------------------------------------------------------------>
import { useRouter } from "next/navigation";
import { useDispatch, useSelector } from "react-redux";
import { useCallback, useEffect, useState } from "react";
import { Col, Row, Space, Image, Layout, message, Typography } from "antd";

//? Modules -------------------------------------------------------------------->
import Wallet from "./Wallet/Wallet";
import AddressBook from "./AddressBook/AddressBook";
import EditProfile from "./EditProfile/EditProfile";
import RewardPoints from "./RewardPoints/RewardPoints";
import WishlistModule from "./WishlistModule/WishlistModule";
import DashboardModule from "./DashboardModule/DashboardModule";
import OrdersListingModule from "./OrdersListingModule/OrdersListingModule";
import DynamicButton from "@/components/shared/Buttons/DynamicButton/DynamicButton";

//? Assets --------------------------------------------------------------------->
import "./AccountsPage.scss";

//? Redux Reducers ------------------------------------------------------------->
import AccountStatus, { customerLogout } from "@/functions/Cookies/Cookies";
import {
  logoutReducer,
  changeActiveMenuReducer,
} from "@/redux/Slices/accountSlice";
import Link from "next/link";

//? Library Constants ---------------------------------------------------------->
const { Text } = Typography;
const { Content, Sider } = Layout;

export default function AccountsPage({ userAgent }) {
  //? Hooks ---------------------------------------------------------------------->
  const router = useRouter();
  const dispatch = useDispatch();
  const {
    loggedIn,
    comverse_customer_id,
    comverse_customer_email,
    comverse_customer_token,
  } = AccountStatus();

  //? Redux States --------------------------------------------------------------->
  const comverse_customer_name = useSelector(
    (state) => state.accountSlice?.comverse_customer_name
  );
  // const comverse_customer_token = useSelector(
  //   (state) => state.accountSlice?.comverse_customer_token
  // );
  // const loggedIn = useSelector((state) => state.accountSlice.loggedIn);
  const activeMenuValueFromRedux = useSelector(
    (state) => state.accountSlice.activeMenu
  );

  //? States --------------------------------------------------------------------->
  const [Loading, setLoading] = useState(true);
  const [ActiveMenu, setActiveMenu] = useState(activeMenuValueFromRedux);

  //? Orders Listiing States ----------------------------------------------------->
  const [OrdersList, setOrdersList] = useState([]);
  const [OrdersDetail, setOrdersDetail] = useState({});
  const [DashboardDetails, setDashboardDetails] = useState({});

  //? Wishlist Module States ----------------------------------------------------->
  const [WishList, setWishList] = useState([]);
  const [WishlistDetail, setWishlistDetail] = useState({});

  //? Functions ------------------------------------------------------------------>
  const logOut = useCallback(() => {
    customerLogout();
    dispatch(logoutReducer());
    router.push("/");
  }, [dispatch, router]);

  const fetchDashboardDetails = useCallback(async () => {
    if (comverse_customer_token) {
      try {
        const headers = {
          "Content-Type": "application/json",
        };
        if (comverse_customer_token) {
          headers.Authorization = comverse_customer_token;
        }

        const payload = {
          method: "GET",
          headers: headers,
        };

        const res = await fetch(
          process.env.NEXT_PUBLIC_BACKEND + "/storefront/customer_dashboard",
          payload
        );

        const data = await res.json();

        if (!res.ok) {
          if (data?.detail === "Invalid auth token") {
            customerLogout();
            dispatch(logoutReducer());
            router.push("/login");
          }
          //logout here
          throw new Error("Api Failed!");
        }

        setDashboardDetails(data);
        return data;
      } catch (error) {
        console.error("Error fetching data:", error.message);
        return null;
      }
    }
  }, [comverse_customer_token]);

  useEffect(() => {
    if (comverse_customer_token) {
      fetchDashboardDetails();
    }
  }, [comverse_customer_token, fetchDashboardDetails]);

  const fetchOrderDetails = useCallback(
    async (pageNo) => {
      if (comverse_customer_token) {
        try {
          setLoading(true);

          let urlToHit = pageNo
            ? `/storefront/customer_order_list?page=${pageNo}&limit=${6}&storefront=23`
            : `/storefront/customer_order_list?storefront=23`;

          const res = await fetch(process.env.NEXT_PUBLIC_BACKEND + urlToHit, {
            headers: {
              "Content-Type": "application/json",
              Authorization: comverse_customer_token,
            },
          });

          const OrdersDetail = await res?.json();

          if (
            OrdersDetail?.detail === "Invalid auth token" ||
            OrdersDetail?.detail ===
              "Session Expired, Please login your account"
          ) {
            logOut();
            // message.error("Session Expired");
          } else if (!res.ok) {
            throw new Error("Accounts Api Failed!");
          }

          if (pageNo) {
            // setOrdersList((prevOrders) => [
            //   ...prevOrders,
            //   ...OrdersDetail?.results,
            // ]);
            setOrdersList(OrdersDetail?.results);
            setLoading(false);
          } else {
            setOrdersDetail(OrdersDetail);
            setOrdersList(OrdersDetail?.results);
            setLoading(false);
          }

          return OrdersDetail;
        } catch (error) {
          console.log("Error fetching data:", error);
          setTimeout(() => {
            setLoading(false);
          }, 1000);
          return null;
        }
      }
    },
    [comverse_customer_token, logOut]
  );

  const fetchWishlistDetails = useCallback(
    async (pageNo) => {
      if (comverse_customer_token) {
        try {
          setLoading(true);

          let urlToHit = pageNo
            ? `/storefront/wishlist_list?page=${pageNo}&limit=${6}&storefront=23`
            : `/storefront/wishlist_list?limit=${6}&storefront=23`;

          const res = await fetch(process.env.NEXT_PUBLIC_BACKEND + urlToHit, {
            headers: {
              "Content-Type": "application/json",
              Authorization: comverse_customer_token,
            },
          });

          const WishlistDetail = await res?.json();

          if (
            WishlistDetail?.detail === "Invalid auth token" ||
            WishlistDetail?.detail ===
              "Session Expired, Please login your account"
          ) {
            logOut();
            // message.error("Session Expired");
          } else if (!res.ok) {
            throw new Error("Accounts Api Failed!");
          }

          if (pageNo) {
            // setWishList((prevList) => [...prevList, ...WishlistDetail?.results]);
            setWishList(WishlistDetail?.results);
            setLoading(false);
          } else {
            setWishlistDetail(WishlistDetail);
            setWishList(WishlistDetail?.results);
            setLoading(false);
          }

          return WishlistDetail;
        } catch (error) {
          console.log("Error fetching data:", error);
          setTimeout(() => {
            setLoading(false);
          }, 1000);
          return null;
        }
      }
    },
    [comverse_customer_token, logOut]
  );

  //? Life Cycles ---------------------------------------------------------------->

  useEffect(() => {
    dispatch(changeActiveMenuReducer(ActiveMenu));
  }, [ActiveMenu]);

  return (
    <>
      {loggedIn
        ? loggedIn && (
            <Row
              className="AccountsPage padding50 marginBottom30"
              style={{ overflow: "hidden", width: "100%" }}
            >
              <Col span={24}>
                <Layout className="site-layout-background">
                  <Sider>
                    <Col className="sideBar">
                      <Col span={24} className="padding0">
                        <Col className="padding15">
                          <Col
                            style={{
                              textAlign: "start",
                              color: "#5A7184",
                              cursor: "pointer",
                              marginBottom: "10px",
                            }}
                          >
                            <Link href="/" className="font11 ">
                              {"< Back To Home"}
                            </Link>
                          </Col>

                          <Space direction="vertical">
                            <Text className="margin0 font18Bold">Account</Text>
                            <Text className="margin0 font18">
                              {DashboardDetails?.profile
                                ? DashboardDetails?.profile?.full_name
                                : "N/A"}
                            </Text>
                          </Space>
                        </Col>

                        <Col>
                          {/* <Menu
                            items={items}
                            defaultSelectedKeys={ActiveMenu}
                          /> */}

                          <Row>
                            <Col
                              span={24}
                              onClick={() => setActiveMenu("dashboard")}
                              className={
                                ActiveMenu === "dashboard"
                                  ? "selectedMenuItem cursorPointer borderTop paddingTop10 paddingBottom10 paddingLeft20"
                                  : "cursorPointer borderTop paddingTop10 paddingBottom10 paddingLeft20"
                              }
                            >
                              <Text>Dashboard</Text>
                            </Col>
                            <Col
                              span={24}
                              onClick={() => setActiveMenu("orders")}
                              className={
                                ActiveMenu === "orders"
                                  ? "selectedMenuItem cursorPointer borderTop paddingTop10 paddingBottom10 paddingLeft20"
                                  : "cursorPointer borderTop paddingTop10 paddingBottom10 paddingLeft20"
                              }
                            >
                              <Text>My Order</Text>
                            </Col>
                            <Col
                              span={24}
                              onClick={() => setActiveMenu("wishlist")}
                              className={
                                ActiveMenu === "wishlist"
                                  ? "selectedMenuItem cursorPointer borderTop paddingTop10 paddingBottom10 paddingLeft20"
                                  : "cursorPointer borderTop paddingTop10 paddingBottom10 paddingLeft20"
                              }
                            >
                              <Text>Wishlist</Text>
                            </Col>
                            <Col
                              span={24}
                              onClick={() => setActiveMenu("address")}
                              className={
                                ActiveMenu === "address"
                                  ? "selectedMenuItem cursorPointer borderTop paddingTop10 paddingBottom10 paddingLeft20"
                                  : "cursorPointer borderTop paddingTop10 paddingBottom10 paddingLeft20"
                              }
                            >
                              <Text>Address Book</Text>
                            </Col>
                            <Col
                              span={24}
                              onClick={() => setActiveMenu("points")}
                              className={
                                ActiveMenu === "points"
                                  ? "selectedMenuItem cursorPointer borderTop paddingTop10 paddingBottom10 paddingLeft20"
                                  : "cursorPointer borderTop paddingTop10 paddingBottom10 paddingLeft20"
                              }
                            >
                              <Text>Reward Point</Text>
                            </Col>
                            <Col
                              span={24}
                              onClick={() => setActiveMenu("wallet")}
                              className={
                                ActiveMenu === "wallet"
                                  ? "selectedMenuItem cursorPointer borderTop paddingTop10 paddingBottom10 paddingLeft20"
                                  : "cursorPointer borderTop paddingTop10 paddingBottom10 paddingLeft20"
                              }
                            >
                              <Text>My Wallet</Text>
                            </Col>
                            <Col
                              span={24}
                              onClick={() => {
                                setActiveMenu("faq");
                                window.open("/faq", "_blank");
                              }}
                              className={
                                ActiveMenu === "faq"
                                  ? "selectedMenuItem cursorPointer borderTop paddingTop10 paddingBottom10 paddingLeft20"
                                  : "cursorPointer borderTop paddingTop10 paddingBottom10 paddingLeft20"
                              }
                            >
                              <Text>FAQs</Text>
                            </Col>
                            <Col
                              span={24}
                              onClick={() => {
                                setActiveMenu("about");
                                window.open("/about-us", "_blank");
                              }}
                              className={
                                ActiveMenu === "about"
                                  ? "selectedMenuItem about-us cursorPointer borderTop paddingTop10 paddingBottom10 paddingLeft20"
                                  : "cursorPointer borderTop paddingTop10 paddingBottom10 paddingLeft20"
                              }
                            >
                              <Text>About Us</Text>
                            </Col>
                            <Col
                              span={24}
                              onClick={() => {
                                window.open("#", "_blank");
                                setActiveMenu("terms");
                              }}
                              className={
                                ActiveMenu === "terms"
                                  ? "selectedMenuItem cursorPointer borderTop paddingTop10 paddingBottom10 paddingLeft20"
                                  : "cursorPointer borderTop paddingTop10 paddingBottom10 paddingLeft20"
                              }
                            >
                              <Text>Terms & Conditions</Text>
                            </Col>
                            <Col
                              span={24}
                              onClick={() => {
                                window.open(
                                  "/pages/static/privacy-policy",
                                  "_blank"
                                );
                                setActiveMenu("privacy");
                              }}
                              className={
                                ActiveMenu === "privacy"
                                  ? "selectedMenuItem cursorPointer borderTop paddingTop10 paddingBottom10 paddingLeft20"
                                  : "cursorPointer borderTop paddingTop10 paddingBottom10 paddingLeft20"
                              }
                            >
                              <Text>Privacy Policy</Text>
                            </Col>
                          </Row>
                        </Col>
                      </Col>

                      <Col span={24}>
                        <DynamicButton
                          name="Logout"
                          // type="primary"
                          // property="danger"
                          function={logOut}
                          style={{
                            border: "none",
                            color: "#BC1B41",
                            background: "none",
                            justifyContent: "start",
                          }}
                        />
                      </Col>
                    </Col>
                  </Sider>

                  <Content style={{ minHeight: "350px" }}>
                    {ActiveMenu === "dashboard" ? (
                      <Col>
                        <DashboardModule
                          Loading={Loading}
                          userAgent={userAgent}
                          OrdersDetail={OrdersDetail}
                          setActiveMenu={setActiveMenu}
                        />
                      </Col>
                    ) : ActiveMenu === "orders" ? (
                      <Col>
                        <OrdersListingModule
                          userAgent={userAgent}
                          Loading={Loading}
                          OrdersList={OrdersList}
                          OrdersDetail={OrdersDetail}
                          setActiveMenu={setActiveMenu}
                          fetchOrderDetails={fetchOrderDetails}
                        />
                      </Col>
                    ) : ActiveMenu === "wishlist" ? (
                      <Col>
                        <WishlistModule
                          userAgent={userAgent}
                          Loading={Loading}
                          WishList={WishList}
                          WishlistDetail={WishlistDetail}
                          setActiveMenu={setActiveMenu}
                          fetchWishlistDetails={fetchWishlistDetails}
                        />
                      </Col>
                    ) : ActiveMenu === "points" ? (
                      <Col>
                        <RewardPoints
                          Loading={Loading}
                          setActiveMenu={setActiveMenu}
                        />
                      </Col>
                    ) : ActiveMenu === "wallet" ? (
                      <Col>
                        <Wallet
                          Loading={Loading}
                          setActiveMenu={setActiveMenu}
                        />
                      </Col>
                    ) : // ) : ActiveMenu === "faq" ? (
                    //   <Col>FAQs</Col>
                    // ) : ActiveMenu === "about" ? (
                    //   <Col>About Us</Col>
                    // ) : ActiveMenu === "terms" ? (
                    //   <Col>Terms & Conditions</Col>
                    // ) : ActiveMenu === "privacy" ? (
                    //   <Col>Privacy Policy</Col>
                    ActiveMenu === "address" ? (
                      <Col>
                        <AddressBook
                          Loading={Loading}
                          setActiveMenu={setActiveMenu}
                        />
                      </Col>
                    ) : ActiveMenu === "edit" ? (
                      <Col>
                        <EditProfile setActiveMenu={setActiveMenu} />
                      </Col>
                    ) : null}
                  </Content>
                </Layout>
              </Col>

              {/* <Col span={24} style={{ textAlign: "center" }}>
                <Text>AlfaMall - Copyright © 2023</Text>
              </Col>

              <Col
                span={24}
                style={{ textAlign: "center" }}
                className="marginTop20"
              >
                <Space size="middle">
                  <Image src={applePay} alt="PaymentMethods" />
                  <Image src={paypal} alt="PaymentMethods" />
                  <Image src={visa} alt="PaymentMethods" />
                  <Image src={mastercard} alt="PaymentMethods" />
                </Space>
              </Col> */}
            </Row>
          )
        : router.push("/")}
    </>
  );
}
